import React, { useMemo } from 'react';
import { Avatar, Divider, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import cx from 'classnames';

import { useAppSelector } from 'store/hooks';
import { ChatsSelector } from 'store/chats/selectors';

import { TICKET } from 'router/routes';
import { getFormattedDate } from 'utils/dates';

import { ITicket } from 'services/tickets/types';

import { ReactComponent as PulseIcon } from 'assets/pulse.icon.svg';
import { ReactComponent as TicketIcon } from 'assets/tickets.icon.svg';
import { ReactComponent as CompanyIcon } from 'assets/company.icon.svg';

import Chip from 'pages/Teams/components/Chip';

import useStyles from '../styles';

interface IProps extends ITicket {}

const Ticket: React.FC<IProps> = ({
  id,
  ticketNumber,
  title,
  status,
  companyName,
  chatId,
  company,
}) => {
  const classes = useStyles();
  const chats = useAppSelector(ChatsSelector);
  const navigate = useNavigate();
  const { ticketId } = useParams();

  const chat = useMemo(() => {
    if (!chatId) return null;

    return chats.find(chat => +chat.id === chatId);
  }, [chats, chatId]);

  const handleTicketSelection = (id: string): void => {
    navigate(TICKET.replace(':ticketId', ticketNumber ?? id));
  };

  const isActiveChat = ticketId === String(ticketNumber) || ticketId === String(id);

  return (
    <div
      key={ticketNumber ?? id}
      className={cx(classes.ticket, {
        [classes.active]: isActiveChat,
      })}
      onClick={() => {
        handleTicketSelection(String(ticketNumber ?? id));
      }}
    >
      <div className={classes.ticketListItem}>
        <Typography className={classes.ticketTitle} noWrap>
          <b>{title}</b>
        </Typography>
        <Chip icon={<TicketIcon />} title={`#${ticketNumber ?? id}`} />
      </div>
      <div className={classes.chipContainer}>
        <Chip icon={<PulseIcon />} title={status} />
        <Chip icon={<CompanyIcon />} title={companyName || company} />
      </div>

      {chat && (
        <>
          <Divider orientation="horizontal" />

          <div className={classes.chat}>
            <div>
              <Avatar src={chat.lastMessage?.resourceName ? chat.techProfileImg : undefined} />
            </div>
            <div className={classes.chatMessage}>
              <div className={classes.chatMessageSender}>
                <Typography noWrap variant="body1" component="div">
                  <b className={cx({ [classes.contrastText]: isActiveChat })}>
                    {chat?.lastMessage?.contactName
                      ? chat?.lastMessage?.contactName
                      : chat?.lastMessage?.resourceName}
                  </b>
                </Typography>
                <div
                  className={cx(classes.chatMessageDate, { [classes.contrastText]: isActiveChat })}
                >
                  {getFormattedDate(chat?.lastMessage?.dateTime ?? '')}
                </div>
              </div>

              <Typography
                noWrap
                variant="body1"
                component="div"
                className={cx({ [classes.contrastText]: isActiveChat })}
              >
                {chat.lastMessage?.message}
              </Typography>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Ticket;
